import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { MapMovedEvent, Map } from '@sembo-private/maps';
import type { MapComponent } from '@sembo-private/vue3-maps';

import { MapStateKey } from '@apps/main/modules/state/state';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';

import {
  getLocationAndDestinationMarkers,
  getMarkerFormatters,
} from './tripOverviewMapStoreLogic';

type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useTripOverviewMapStore = defineStore(
  'TripOverviewMapStore',
  () => {
    const mapState = injectStrict(MapStateKey);
    const statsigEventsStore = useStatsigEventsStore();

    // State
    const isMapCreated = ref<boolean>(false);
    const isFirstMapRendered = ref<boolean>(true);
    const shouldShowFullScreenMap = ref<boolean>(false);

    // Actions
    function mapViewChanged(event: MapMovedEvent): void {
      mapState.setMapMovedAndCreateNewPolygon(event);
    }

    function setDynamicMapIntegration(integration: Map): void {
      isFirstMapRendered.value = false;
      isMapCreated.value = true;
      mapState.setMapIntegration(integration);
    }

    function openFullScreenDynamicTripMap(): void {
      statsigEventsStore.logTripOverviewOpenFullScreenMap();
      mapState.mapShouldCenterOnMarkers.value = false;
      shouldShowFullScreenMap.value = true;
    }

    function closeFullScreenDynamicTripMap(): void {
      shouldShowFullScreenMap.value = false;
      mapState.mapShouldCenterOnMarkers.value = true;
    }

    // Getters
    const mapProperties = computed<MapProperties>(() => {
      const markers = getLocationAndDestinationMarkers(mapState.markers.value);
      const startRectangle =
        markers.length === 0 ? mapState.DEFAULT_RECTANGLE : undefined;

      return {
        markers,
        markerFormatters: getMarkerFormatters(),
        route: mapState.mapRoute.value,
        showAllMarkers: mapState.mapShouldCenterOnMarkers.value,
        startRectangle,
        gestureHandling: 'greedy',
        locale: mapLanguageLocale,
      };
    });

    return {
      // State
      isFirstMapRendered,
      isMapCreated,
      shouldShowFullScreenMap,

      // Actions
      mapViewChanged,
      setDynamicMapIntegration,
      openFullScreenDynamicTripMap,
      closeFullScreenDynamicTripMap,

      // Getters
      mapProperties,
    };
  }
);
